import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  IconButton,
  Tooltip,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Assessment as AssessmentIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { decryptText, decryptAppId } from "../../../utils/encryption";
import FileUploadComponent from "../../../components/FileUploadComponent";
import s3Utils from "../../../utils/s3Utils";
import PageTitle from "../../../components/PageTitle";
import useAssessmentTracking from '../../../hooks/useAssessmentTracking';
import { LocationWarningDialog, HourlyAttendanceDialog } from '../../../components/AssessmentTrackingDialogs';
import CommonAPIService from "../../../services/commonAPIService";
import { object } from "yup";

// Tab Panel component

const TravelClaimForm = () => {
  const location = useLocation();

  const getOrgIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedOrgId = searchParams.get("orgId");
    if (encryptedOrgId) {
      try {
        const decryptedOrgId = decryptText(encryptedOrgId);
        console.log("📍 Using decrypted orgId from URL query:", decryptedOrgId);
        return decryptedOrgId;
      } catch (error) {
        return encryptedOrgId;
      }
    }
    return null;
  };

  // Function to get appId from URL query parameters with decryption
  const getAppIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAppId = searchParams.get("appId");
    if (encryptedAppId) {
      try {
        const decryptedAppId = decryptAppId(encryptedAppId);
        return decryptedAppId;
      } catch (error) {
        return encryptedAppId;
      }
    }
    return null;
  };

  // Function to get assessmentId from URL query parameters with decryption
  const getAssessmentIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessmentId = searchParams.get("assessmentId");
    if (encryptedAssessmentId) {
      try {
        const decryptedAssessmentId = decryptText(encryptedAssessmentId);
        console.log(
          "📍 Using decrypted assessmentId from URL query:",
          decryptedAssessmentId
        );
        return decryptedAssessmentId;
      } catch (error) {
        return encryptedAssessmentId;
      }
    }
    return null;
  };

  // Function to get assessorId from URL query parameters with decryption
  const getAssessorIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessorId = searchParams.get("assessorId");
    if (encryptedAssessorId) {
      try {
        const decryptedAssessorId = decryptText(encryptedAssessorId);
        console.log(
          "📍 Using decrypted assessorId from URL query:",
          decryptedAssessorId
        );
        return decryptedAssessorId;
      } catch (error) {
        return encryptedAssessorId;
      }
    }
    return null;
  };

  const getOrgId = () => {
    const queryOrgId = getOrgIdFromQuery();
    if (queryOrgId) {
      return queryOrgId;
    }
    return null;
  };

  const getAppId = () => {
    const queryAppId = getAppIdFromQuery();
    if (queryAppId) {
      return queryAppId;
    }
    return null;
  };

  const getAssessmentId = () => {
    const queryAssessmentId = getAssessmentIdFromQuery();
    if (queryAssessmentId) {
      return queryAssessmentId;
    }
    return null;
  };

  const getAssessorId = () => {
    const queryAssessorId = getAssessorIdFromQuery();
    if (queryAssessorId) {
      return queryAssessorId;
    }
    return null;
  };

  const [orgId, setOrgId] = useState(getOrgId());
  const [appId, setAppId] = useState(getAppId());
  const [assessmentId, setAssessmentId] = useState(getAssessmentId());
  const [assessorId, setAssessorId] = useState(getAssessorId());
  const [submittedClaims, setSubmittedClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bankDetailsLoading, setBankDetailsLoading] = useState(false);
  const [bankDetailsExist, setBankDetailsExist] = useState(false);
  const [editingClaimId, setEditingClaimId] = useState(null);
  const [isBankDetailsUpdate, setIsBankDetailsUpdate] = useState(false);
  const [isBankDetailsUpdateExist, setIsBankDetailsUpdateExit] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Assessment tracking hook for location and hourly attendance
  const {
    locationWarningOpen,
    distanceMessage,
    attendanceCheckDialogOpen,
    attendanceCheckMessage,
    setLocationWarningOpen,
    setAttendanceCheckDialogOpen,
    checkHourlyAttendance,
  } = useAssessmentTracking({
    assessorId,
    appId,
    orgId,
  });

  const [formData, setFormData] = useState({
    bankDetails: {
      bankName: "",
      bankAddress: "",
      accountNo: "",
      ifscCode: "",
      panNo: "",
      bankFile: null,
      honorarium: "Yes",
      ownCarRegnNo: "",
    },
    travelSegments: {
      mode: "",
      departureDate: "",
      arrivalDate: "",
      departureFrom: "",
      arrivalAt: "",
      departureTime: "",
      arrivalTime: "",
      amount: "",
      hasOriginalBills: "",
      uploadBill: null,
      segmentCarRegnNo: "",
    },
  });

  // Fetch bank details
  const fetchBankDetails = async () => {
    if (!assessmentId || !assessorId) return;

    try {
      setBankDetailsLoading(true);
      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetAssessorbank?assessmentid=${assessmentId}&assessorid=${assessorId}`;
      const response = await axios.get(url);

      if (response.data && response.data.isstatus && response.data) {
        const data = response.data;
        setBankDetailsExist(true);
        setIsBankDetailsUpdateExit(false);
        setFormData((prev) => ({
          ...prev,
          bankDetails: {
            bankName: data.bankName || '',
            bankAddress: data.bankAddress || '',
            accountNo: data.accountNo || '',
            ifscCode: data.ifscCode || '',
            panNo: data.pan || '',
            bankFile: createFileObject(data.cancelChequeUploadFile) || null,
            honorarium: data.isHonorarium || '',
            ownCarRegnNo: data.carRegNo || '',
          },
        }));
      }else {
        const bankResponse = await CommonAPIService.getAssessorBankDetails(assessorId);
        if (bankResponse.success && bankResponse.data) {
        const data = bankResponse.data;
        setBankDetailsExist(true);
        setIsBankDetailsUpdateExit(true);
        setFormData((prev) => ({
        ...prev,
        bankDetails: {
        bankName: data.Bank_Details || '',
        bankAddress: data.Bank_Address || '',
        accountNo: data.Account_No || '',
        ifscCode: data.IFCS_Code || '',
        panNo: data.PanUploadFile || '',
        bankFile: createFileObject(data.CancelChequeUploadfile) || null,
        honorarium: data.Is_Honoranium || '',
        ownCarRegnNo: data.Car_RegNo || '',
       },
      }));
       } else {
        setBankDetailsExist(false);
        setIsBankDetailsUpdateExit(false);
      }
     }
    } catch (error) {
      console.error('Error fetching bank details:', error);
    } finally {
      setBankDetailsLoading(false);
    }
  };

  const createFileObject = (fileName) => {
    if (!fileName) return null;
    return {
      fileName: fileName,
      key: `live/${fileName}`,
      originalName: fileName,
    };
  };

  // Fetch travel claims
  const fetchTravelClaims = async () => {
    if (!assessmentId || !assessorId) return;

    try {
      setLoading(true);
      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetAssessorTravel?assessmentid=${assessmentId}&assessorid=${assessorId}`;
      const response = await axios.get(url);

      if (response.data && response.data.isstatus && response.data.travel) {
        const claims = Array.isArray(response.data.travel)
          ? response.data.travel
          : [response.data.travel];
        const formattedClaims = await Promise.all(
          claims.map(async (claim) => {
            let billFile = claim.bill;

            if (s3Utils.isJustFilename(claim.bill)) {
              billFile = await s3Utils.generateSignedUrlForFilename(claim.bill);
            }

            return {
              id: claim.id,
              submittedAt: new Date(
                claim.createdAt || Date.now()
              ).toLocaleString(),
              travelSegments: {
                mode: claim.modeOfTravel || "",
                departureDate: claim.dep_Date || "",
                arrivalDate: claim.arrival_Date || "",
                departureFrom: claim.dep_From || "",
                arrivalAt: claim.arrival_To || "",
                departureTime: claim.dep_Time || "",
                arrivalTime: claim.arrival_Time || "",
                amount: claim.amount || "",
                hasOriginalBills: claim.is_Origional_Bill==="1" ? "Yes" : "No",
                uploadBill: billFile || "",
                description: claim.description || "",
              },
              status: "Submitted",
            };
          })
        );

        setSubmittedClaims(formattedClaims);
      }
    } catch (error) {
      console.error('Error fetching travel claims:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (assessmentId && assessorId) {
      fetchBankDetails();
      fetchTravelClaims();
    }
  }, [assessmentId, assessorId]);

  const handleBankDetailChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      bankDetails: {
        ...prev.bankDetails,
        [field]: value,
      },
    }));
  };

  const handleTravelSegmentChange = (field, value) => {
    console.log("Updating travel segment:", field, value);
    setFormData((prev) => ({
      ...prev,
      travelSegments: {
        ...prev.travelSegments,
        [field]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!assessorId || !assessmentId || !orgId || !appId) {
      showSnackbar('Missing required parameters', 'error');
      return;
    }
    if (!formData.travelSegments.mode) {
      showSnackbar('Please select Mode of Travel', 'error');
      return;
    }

    if(!isBankDetailsUpdate){
      await handleSubmitBankDetails(2);
    }

    // Validate Upload Bill is mandatory based on mode
    const mode = formData.travelSegments.mode;
    const hasOriginalBills = formData.travelSegments.hasOriginalBills;
    
    if (mode !== 'No Claim') {
      // For Road mode, bill is mandatory only if hasOriginalBills is "Yes" or amount is greater than 0
      if (mode === 'Road') {
        // if ((hasOriginalBills === 'Yes' || formData.travelSegments.amount > 0) && !formData.travelSegments.uploadBill) {
        if (formData.travelSegments.amount > 0 && !formData.travelSegments.uploadBill) {
          showSnackbar('Please upload bill before submitting', 'error');
          return;
        }
      } else {
        // For Air, Rail, and Others modes, bill is always mandatory
        if (!formData.travelSegments.uploadBill) {
          showSnackbar('Please upload bill before submitting', 'error');
          return;
        }
      }
    }

    setLoading(true);
    try {
      const payload = {
        assessmentid: String(assessmentId),
        assessorid: String(assessorId),
        orgId: String(orgId),
        appId: String(appId),
        is_Origional_Bill: formData.travelSegments.hasOriginalBills==="Yes" ? "1" : "0",
        dep_Date: formData.travelSegments.departureDate,
        dep_From: formData.travelSegments.departureFrom,
        dep_Time: formData.travelSegments.departureTime,
        arrival_Date: formData.travelSegments.arrivalDate,
        arrival_To: formData.travelSegments.arrivalAt,
        arrival_Time: formData.travelSegments.arrivalTime,
        modeOfTravel: formData.travelSegments.mode,
        amount: formData.travelSegments.amount || '0',
        distance: '0',
        bill: formData.travelSegments.uploadBill ? editingClaimId ? formData.travelSegments.uploadBill.fileName || formData.travelSegments.uploadBill: formData.travelSegments.uploadBill : '',
        description: formData.travelSegments.mode === 'No Claim' ? 'No Travel Claim' : (formData.travelSegments.description || ''),
      };

      let response;
      if (editingClaimId) {
        // Update existing claim
        const updatePayload = { id: String(editingClaimId), ...payload };
        const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/UpdateAssessorTravel`;
        response = await axios.put(url, updatePayload);
      } else {
        // Create new claim
        const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/SaveAssessorTravel`;
        response = await axios.post(url, payload);
      }

      if (response.data.success || response.data.isstatus) {
        showSnackbar(
          editingClaimId ? 'Travel claim updated successfully!' : 'Travel claim submitted successfully!',
          'success'
        );
        
        // Reset travel segments form and editing state
        setFormData((prev) => ({
          ...prev,
          travelSegments: {
            mode: '',
            departureDate: '',
            arrivalDate: '',
            departureFrom: '',
            arrivalAt: '',
            departureTime: '',
            arrivalTime: '',
            amount: '',
            hasOriginalBills: '',
            uploadBill: null,
            description: '',
          },
        }));
        setEditingClaimId(null);
        setIsBankDetailsUpdate(true);
        // Refresh travel claims list
        await fetchTravelClaims();
      } else {
        throw new Error(response.data.message || 'Failed to save travel claim');
      }
    } catch (error) {
      console.error('Error submitting travel claim:', error);
      showSnackbar(
        error.response?.data?.message || error.message || 'Error submitting travel claim',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const getModeColor = (mode) => {
    const colors = {
      Air: "primary",
      Rail: "secondary",
      Road: "warning",
      Others: "info",
      "No Claim": "default",
    };
    return colors[mode] || "default";
  };

  const editClaim = (claim) => {
    setEditingClaimId(claim.id);
    setFormData((prev) => ({
      ...prev,
      travelSegments: {
        mode: claim.travelSegments.mode,
        departureDate: claim.travelSegments.departureDate,
        arrivalDate: claim.travelSegments.arrivalDate,
        departureFrom: claim.travelSegments.departureFrom,
        arrivalAt: claim.travelSegments.arrivalAt,
        departureTime: claim.travelSegments.departureTime,
        arrivalTime: claim.travelSegments.arrivalTime,
        amount: claim.travelSegments.amount,
        hasOriginalBills: claim.travelSegments.hasOriginalBills,
        uploadBill: claim.travelSegments.uploadBill?createFileObject(claim.travelSegments.uploadBill.split("/").pop()):null,
        description: claim.travelSegments.description || '',
      },
    }));
    showSnackbar('Claim loaded for editing. Update and submit to save changes.', 'info');
  };

  const deleteClaim = async (claimId) => {
    if (!window.confirm("Are you sure you want to delete this travel claim?")) {
      return;
    }

    if (!assessmentId || !assessorId) {
      showSnackbar('Missing required parameters', 'error');
      return;
    }

    try {
      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/DeleteAssessorTravel?id=${claimId}&assessmentid=${assessmentId}&assessorid=${assessorId}`;
      const response = await axios.delete(url);

      if (response.data.success || response.data.isstatus) {
        showSnackbar('Travel claim deleted successfully!', 'success');
        // Remove from local state
        setSubmittedClaims((prev) =>
          prev.filter((claim) => claim.id !== claimId)
        );
      } else {
        throw new Error(response.data.message || 'Failed to delete travel claim');
      }
    } catch (error) {
      console.error('Error deleting travel claim:', error);
      showSnackbar(
        error.response?.data?.message || error.message || 'Error deleting travel claim',
        'error'
      );
    }
  };

  const handleSubmitBankDetails = async (type) => {
    if (!assessorId || !assessmentId) {
      showSnackbar("Missing required parameters", "error");
      return;
    }
    if(!formData.bankDetails.bankFile){
      showSnackbar("Please upload Cancel Cheque/Bank file upload", "error");
      return;
    }
    setBankDetailsLoading(true);
    try {
      const payload = {
        assessorId: String(assessorId),
        assessmentId: String(assessmentId),
        bankName: formData.bankDetails.bankName,
        bankAddress: formData.bankDetails.bankAddress,
        accountNo: formData.bankDetails.accountNo,
        ifscCode: formData.bankDetails.ifscCode,
        carRegNo: formData.bankDetails.ownCarRegnNo,
        isHonorarium: formData.bankDetails.honorarium,
        pan: formData.bankDetails.panNo,
        cancelChequeUploadFile: typeof formData.bankDetails.bankFile === "object"? formData.bankDetails.bankFile.fileName : formData.bankDetails.bankFile || "",
        type: bankDetailsExist ? (isBankDetailsUpdateExist ? "Save" : "Update") : "Save",
      };
      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/SaveAssessorbank`;
      const response = await axios.post(url, payload);
      if (response.data.success || response.data.isstatus) {
        if(type===1){
         showSnackbar(bankDetailsExist? "Bank details updated successfully!": "Bank details saved successfully!","success");
         await fetchBankDetails();
        }
        setIsBankDetailsUpdate(true);
      } else {
        throw new Error(response.data.message || "Failed to save bank details");
        setIsBankDetailsUpdate(false);
      }
    } catch (error) {
      setIsBankDetailsUpdate(false);
      console.error("Error saving bank details:", error);
      showSnackbar(
        error.response?.data?.message ||
          error.message ||
          "Error saving bank details",
        "error",
      );
    } finally {
      setBankDetailsLoading(false);
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
    <PageTitle title="NABL | TADA" />
    <Box sx={{ p: 2, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="h4"
          sx={{ fontWeight: "bold", color: "primary.main" }}
        >
          TRAVEL CLAIM
        </Typography>
      </Box>

      {/* Bank Details Section */}
      <Card sx={{ mb: 3, boxShadow: 2, borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: "bold", mb: 3, color: "primary.main" }}
          >
            Bank Details
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name of the Bank"
                value={formData.bankDetails.bankName}
                onChange={(e) =>
                  handleBankDetailChange("bankName", e.target.value)
                }
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Bank Address"
                value={formData.bankDetails.bankAddress}
                onChange={(e) =>
                  handleBankDetailChange("bankAddress", e.target.value)
                }
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Account No"
                value={formData.bankDetails.accountNo}
                onChange={(e) =>
                  handleBankDetailChange("accountNo", e.target.value)
                }
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="IFSC Code"
                value={formData.bankDetails.ifscCode}
                onChange={(e) =>
                  handleBankDetailChange("ifscCode", e.target.value)
                }
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="PAN NO"
                value={formData.bankDetails.panNo}
                onChange={(e) =>
                  handleBankDetailChange("panNo", e.target.value)
                }
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FileUploadComponent
                fieldName="bankFile"
                label="Cancel Cheque/Bank file upload"
                orgId={assessorId}
                section="tada-documents"
                required={true}
                accept=".pdf,.jpg,.jpeg,.png"
                maxSize={5}
                multiple={false}
                onChange={(fieldName, fileData) => {
                  handleBankDetailChange("bankFile", fileData ? fileData.fileName : null);
                }}
                initialFile={formData.bankDetails.bankFile}
              />
            </Grid>
            {/* <Grid item xs={12} md={6}>
              <FormControl component="fieldset">
                <FormLabel
                  component="legend"
                  sx={{ fontWeight: "bold", mb: 2 }}
                >
                  Do you want to Take Honorarium?
                </FormLabel>
                <RadioGroup
                  row
                  value={formData.bankDetails.honorarium}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      bankDetails: {
                        ...prev.bankDetails,
                        honorarium: e.target.value,
                      },
                    }))
                  }
                >
                  <FormControlLabel
                    value="Yes"
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel value="No" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            </Grid> */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Travel by own Car Regn. No."
                value={formData.bankDetails.ownCarRegnNo}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    bankDetails: {
                      ...prev.bankDetails,
                      ownCarRegnNo: e.target.value,
                    },
                  }))
                }
                margin="normal"
                placeholder="Enter vehicle registration number"
              />
            </Grid>
          </Grid>

          {/* Bank Details Submit Button */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Button
              variant="contained"
              size="large"
              disabled={bankDetailsLoading}
              sx={{ px: 6, py: 1.5, fontSize: "1rem" }}
              onClick={()=>handleSubmitBankDetails(1)}
            >
              {bankDetailsLoading ? 'Saving...' : bankDetailsExist ? 'Update Bank Details' : 'Submit Bank Details'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit}>
        {/* Travel Details Sections */}
          <Card sx={{ mb: 3, boxShadow: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: "primary.main", mb: 2 }}
              >
                Travel Details
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3, fontStyle: "italic" }}
              >
                (Enclose original bills in support of claim referred)
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl component="fieldset" required>
                    <FormLabel
                      component="legend"
                      sx={{ fontWeight: "bold", mb: 1 }}
                    >
                      Mode of Travel
                    </FormLabel>
                    <RadioGroup
                      row
                      value={formData.travelSegments.mode}
                      onChange={(e) =>
                        handleTravelSegmentChange("mode", e.target.value)
                      }
                    >
                      <FormControlLabel
                        value="Air"
                        control={<Radio />}
                        label="Air"
                      />
                      <FormControlLabel
                        value="Rail"
                        control={<Radio />}
                        label="Rail"
                      />
                      <FormControlLabel
                        value="Road"
                        control={<Radio />}
                        label="Road"
                      />
                      <FormControlLabel
                        value="Others"
                        control={<Radio />}
                        label="Others"
                      />
                      <FormControlLabel
                        value="No Claim"
                        control={<Radio />}
                        label="No Claim"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                {/* Air and Rail Fields */}
                {(formData.travelSegments.mode === "Air" || formData.travelSegments.mode === "Rail") && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Departure Date"
                        type="date"
                        value={formData.travelSegments.departureDate}
                        onChange={(e) =>
                          handleTravelSegmentChange(
                            "departureDate",
                            e.target.value
                          )
                        }
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Arrival Date"
                        type="date"
                        value={formData.travelSegments.arrivalDate}
                        onChange={(e) =>
                          handleTravelSegmentChange(
                            "arrivalDate",
                            e.target.value
                          )
                        }
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Departure From"
                        value={formData.travelSegments.departureFrom}
                        onChange={(e) =>
                          handleTravelSegmentChange(
                            "departureFrom",
                            e.target.value
                          )
                        }
                        margin="normal"
                        required
                        placeholder="Enter departure location"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Arrival At"
                        value={formData.travelSegments.arrivalAt}
                        onChange={(e) =>
                          handleTravelSegmentChange(
                            "arrivalAt",
                            e.target.value
                          )
                        }
                        margin="normal"
                        required
                        placeholder="Enter arrival location"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Departure Time"
                        type="time"
                        value={formData.travelSegments.departureTime}
                        onChange={(e) =>
                          handleTravelSegmentChange(
                            "departureTime",
                            e.target.value
                          )
                        }
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Arrival Time"
                        type="time"
                        value={formData.travelSegments.arrivalTime}
                        onChange={(e) =>
                          handleTravelSegmentChange(
                            "arrivalTime",
                            e.target.value
                          )
                        }
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Amount"
                        type="number"
                        value={formData.travelSegments.amount}
                        onChange={(e) =>
                          handleTravelSegmentChange(
                            "amount",
                            e.target.value
                          )
                        }
                        margin="normal"
                        required
                        placeholder="Enter amount"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FileUploadComponent
                        fieldName="uploadBill"
                        label="Upload Bill"
                        orgId={assessorId}
                        section="tada-documents"
                        required={true}
                        accept=".pdf,.jpg,.jpeg,.png"
                        maxSize={5}
                        multiple={false}
                        onChange={(fieldName, fileData) => {
                          console.log("Selected file data:", fileData);
                          handleTravelSegmentChange(
                            "uploadBill",
                            fileData ? fileData.fileName : null
                          );
                        }}
                        initialFile={formData.travelSegments.uploadBill}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ mt: 1 }}>
                        Note: Please Send Original Bills to NABL
                      </Alert>
                    </Grid>
                  </>
                )}

                {/* Road Fields */}
                {formData.travelSegments.mode === "Road" && (
                  <>
                    {/* <Grid item xs={12}>
                      <FormControl component="fieldset" required>
                        <FormLabel
                          component="legend"
                          sx={{ fontWeight: "bold", mb: 1 }}
                        >
                          Do You have Original Bills?
                        </FormLabel>
                        <RadioGroup
                          row
                          value={formData.travelSegments.hasOriginalBills}
                          onChange={(e) =>
                            handleTravelSegmentChange(
                              "hasOriginalBills",
                              e.target.value
                            )
                          }
                        >
                          <FormControlLabel
                            value="Yes"
                            control={<Radio />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="No"
                            control={<Radio />}
                            label="No"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid> */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Departure Date"
                        type="date"
                        value={formData.travelSegments.departureDate}
                        onChange={(e) =>
                          handleTravelSegmentChange(
                            "departureDate",
                            e.target.value
                          )
                        }
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Arrival Date"
                        type="date"
                        value={formData.travelSegments.arrivalDate}
                        onChange={(e) =>
                          handleTravelSegmentChange(
                            "arrivalDate",
                            e.target.value
                          )
                        }
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Departure From"
                        value={formData.travelSegments.departureFrom}
                        onChange={(e) =>
                          handleTravelSegmentChange(
                            "departureFrom",
                            e.target.value
                          )
                        }
                        margin="normal"
                        required
                        placeholder="Enter departure location"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Arrival At"
                        value={formData.travelSegments.arrivalAt}
                        onChange={(e) =>
                          handleTravelSegmentChange(
                            "arrivalAt",
                            e.target.value
                          )
                        }
                        margin="normal"
                        required
                        placeholder="Enter arrival location"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Departure Time"
                        type="time"
                        value={formData.travelSegments.departureTime}
                        onChange={(e) =>
                          handleTravelSegmentChange(
                            "departureTime",
                            e.target.value
                          )
                        }
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Arrival Time"
                        type="time"
                        value={formData.travelSegments.arrivalTime}
                        onChange={(e) =>
                          handleTravelSegmentChange(
                            "arrivalTime",
                            e.target.value
                          )
                        }
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Amount"
                        type="number"
                        value={formData.travelSegments.amount}
                        onChange={(e) =>
                          handleTravelSegmentChange(
                            "amount",
                            e.target.value
                          )
                        }
                        margin="normal"
                        required
                        placeholder="Enter amount"
                      />
                    </Grid>
                    {/* {(formData.travelSegments.hasOriginalBills === "Yes" || formData.travelSegments.amount > 0) && ( */}
                    {formData.travelSegments.amount > 0 && ( 
                      <>
                        <Grid item xs={12}>
                          <FileUploadComponent
                            fieldName="uploadBill"
                            label="Upload Bill"
                            orgId={assessorId}
                            section="tada-documents"
                            required={true}
                            accept=".pdf,.jpg,.jpeg,.png"
                            maxSize={5}
                            multiple={false}
                            onChange={(fieldName, fileData) => {
                              handleTravelSegmentChange(
                                "uploadBill",
                                fileData ? fileData.fileName : null
                              );
                            }}
                            initialFile={formData.travelSegments.uploadBill}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Alert severity="info" sx={{ mt: 1 }}>
                            Note: Please Send Original Bills to NABL
                          </Alert>
                        </Grid>
                      </>
                    )}
                  </>
                )}

                {/* Others Fields */}
                {formData.travelSegments.mode === "Others" && (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description"
                        value={formData.travelSegments.description}
                        onChange={(e) =>
                          handleTravelSegmentChange(
                            "description",
                            e.target.value
                          )
                        }
                        margin="normal"
                        required
                        multiline
                        rows={3}
                        placeholder="Enter description"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Amount"
                        type="number"
                        value={formData.travelSegments.amount}
                        onChange={(e) =>
                          handleTravelSegmentChange(
                            "amount",
                            e.target.value
                          )
                        }
                        margin="normal"
                        required
                        placeholder="Enter amount"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FileUploadComponent
                        fieldName="uploadBill"
                        label="Upload Bill"
                        orgId={assessorId}
                        section="tada-documents"
                        required={true}
                        accept=".pdf,.jpg,.jpeg,.png"
                        maxSize={5}
                        multiple={false}
                        onChange={(fieldName, fileData) => {
                          handleTravelSegmentChange(
                            "uploadBill",
                            fileData ? fileData.fileName : null
                          );
                        }}
                        initialFile={formData.travelSegments.uploadBill}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ mt: 1 }}>
                        Note: Please Send Original Bills to NABL
                      </Alert>
                    </Grid>
                  </>
                )}

                {/* No Claim Fields */}
                {formData.travelSegments.mode === "No Claim" && (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description"
                        value="No Travel Claim"
                        margin="normal"
                        required
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Amount"
                        type="number"
                        value="0"
                        margin="normal"
                        required
                        disabled
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>

        {/* Submit Button */}
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 4 }}>
          {editingClaimId && (
            <Button
              variant="outlined"
              size="large"
              onClick={() => {
                setEditingClaimId(null);
                setFormData((prev) => ({
                  ...prev,
                  travelSegments: {
                    mode: '',
                    departureDate: '',
                    arrivalDate: '',
                    departureFrom: '',
                    arrivalAt: '',
                    departureTime: '',
                    arrivalTime: '',
                    amount: '',
                    hasOriginalBills: '',
                    uploadBill: null,
                    description: '',
                  },
                }));
                showSnackbar('Edit cancelled', 'info');
              }}
              sx={{ px: 4, py: 1.5, fontSize: "1.1rem" }}
            >
              Cancel Edit
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
            sx={{ px: 6, py: 1.5, fontSize: "1.1rem" }}
          >
            {loading ? (
              <>
                <CircularProgress size={24} sx={{ mr: 1 }} color="inherit" />
                {editingClaimId ? 'Updating...' : 'Submitting...'}
              </>
            ) : (
              editingClaimId ? 'Update Travel Claim' : 'Submit Travel Claim'
            )}
          </Button>
        </Box>
      </form>

      {/* Submitted Travel Claims Table */}
      {submittedClaims.length > 0 && (
        <Card sx={{ mb: 3, boxShadow: 3, borderRadius: 2, mt: 4 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <AssessmentIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                Submitted Travel Claims
              </Typography>
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table sx={{ minWidth: 650 }} aria-label="travel claims table">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "primary.main",
                        color: "white",
                      }}
                    >
                      Submitted On
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "primary.main",
                        color: "white",
                      }}
                    >
                      Mode of Travel
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "primary.main",
                        color: "white",
                      }}
                    >
                      Departure Details
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "primary.main",
                        color: "white",
                      }}
                    >
                      Arrival Details
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "primary.main",
                        color: "white",
                      }}
                    >
                      Amount
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "primary.main",
                        color: "white",
                      }}
                    >
                      Description
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "primary.main",
                        color: "white",
                      }}
                    >
                      Upload Bill
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "primary.main",
                        color: "white",
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {submittedClaims.map((claim) => (
                    <TableRow
                      key={claim.id}
                      sx={{
                        "&:nth-of-type(odd)": {
                          backgroundColor: "#fafafa",
                        },
                        "&:hover": {
                          backgroundColor: "#f0f0f0",
                        },
                      }}
                    >
                      <TableCell sx={{ verticalAlign: "top" }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: "medium" }}
                        >
                          {claim.submittedAt}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        <Chip
                          label={claim.travelSegments.mode}
                          color={getModeColor(claim.travelSegments.mode)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        {claim.travelSegments.departureFrom ? (
                          <>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: "medium" }}
                            >
                              {claim.travelSegments.departureFrom}
                            </Typography>
                            <Typography variant="caption" display="block">
                              {claim.travelSegments.departureDate}
                            </Typography>
                            <Typography variant="caption" display="block">
                              {claim.travelSegments.departureTime}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2">-</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        {claim.travelSegments.arrivalAt ? (
                          <>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: "medium" }}
                            >
                              {claim.travelSegments.arrivalAt}
                            </Typography>
                            <Typography variant="caption" display="block">
                              {claim.travelSegments.arrivalDate}
                            </Typography>
                            <Typography variant="caption" display="block">
                              {claim.travelSegments.arrivalTime}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2">-</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: "medium" }}
                        >
                          {claim.travelSegments.amount ? `₹${claim.travelSegments.amount}` : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        <Typography variant="body2">
                          {claim.travelSegments.description || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        {claim.travelSegments.uploadBill ? (
                            <Tooltip title={claim.travelSegments.uploadBill}>
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => {
                                  if (claim.travelSegments.uploadBill) {
                                   window.open(claim.travelSegments.uploadBill, "_blank");
                                  }
                                }}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                        ) : (
                          <Typography variant="body2">-</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Edit Claim">
                            <IconButton
                              color="primary"
                              onClick={() => editClaim(claim)}
                              size="small"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Claim">
                            <IconButton
                              color="error"
                              onClick={() => deleteClaim(claim.id)}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Entitlement Rules Section */}
      <Card sx={{ mb: 3, boxShadow: 3, borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: "bold", mb: 3, color: "primary.main" }}
          >
            Entitlement for Travel
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
              Travel Guidelines
            </Typography>
          </Alert>

          <Box sx={{ mb: 3 }}>
            <Typography
              variant="body1"
              sx={{ mb: 1, display: "flex", alignItems: "flex-start" }}
            >
              <Box component="span" sx={{ mr: 1, fontWeight: "bold" }}>
                •
              </Box>
              If the journey is more than 300 Kms, travel to be made by Air in
              economy class (Apex fare).
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 1, display: "flex", alignItems: "flex-start" }}
            >
              <Box component="span" sx={{ mr: 1, fontWeight: "bold" }}>
                •
              </Box>
              If the journey is up to 300 Kms, travel to be made by train in 2nd
              AC Class / AC Chair Class or by AC Bus.
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 1, display: "flex", alignItems: "flex-start" }}
            >
              <Box component="span" sx={{ mr: 1, fontWeight: "bold" }}>
                •
              </Box>
              If outstation journey is made by own car, the reimbursement will
              be restricted to 2nd AC class fare by train.
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography
            variant="body1"
            sx={{ mb: 1, display: "flex", alignItems: "flex-start" }}
          >
            <Box component="span" sx={{ mr: 1, fontWeight: "bold" }}>
              •
            </Box>
            Travel within the city by taxi will be reimbursed on production of
            receipts / bills. In absence of taxi bills or travel by own car
            within the city, claim will be reimbursed @ Rs.15 per km.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 1, display: "flex", alignItems: "flex-start" }}
          >
            <Box component="span" sx={{ mr: 1, fontWeight: "bold" }}>
              •
            </Box>
            Any other relevant expenses during the travel will be reimbursed
            only on production of receipts / bills.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
              Boarding and Lodging Entitlement (in case of assessments)
            </Typography>
          </Alert>

          <Typography
            variant="body1"
            sx={{ mb: 1, display: "flex", alignItems: "flex-start" }}
          >
            <Box component="span" sx={{ mr: 1, fontWeight: "bold" }}>
              •
            </Box>
            A single occupancy AC accommodation to be provided for each
            Assessor/ Observer in a reasonably good hotel / guesthouse and
            arrangement for local transportation from temporary residence to the
            CAB site and airport / railway station / bus stand to be made.
          </Typography>
          <Typography
            variant="body1"
            sx={{ display: "flex", alignItems: "flex-start" }}
          >
            <Box component="span" sx={{ mr: 1, fontWeight: "bold" }}>
              •
            </Box>
            The CAB shall pay for meals of Assessor/ Observer during the stay,
            within the reasonable limitations.
          </Typography>
        </CardContent>
      </Card>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Location Warning Dialog */}
      <LocationWarningDialog
        open={locationWarningOpen}
        onClose={() => setLocationWarningOpen(false)}
        distanceMessage={distanceMessage}
      />

      {/* Hourly Attendance Dialog */}
      <HourlyAttendanceDialog
        open={attendanceCheckDialogOpen}
        onClose={() => setAttendanceCheckDialogOpen(false)}
        message={attendanceCheckMessage}
        onRecheck={checkHourlyAttendance}
      />
    </Box>
    </>
  );
};

export default TravelClaimForm;
